import { createRouter, createWebHistory } from 'vue-router'
import store from '~/store'
import routes from './routes'
import { qrValidationGuard, campaignStatusGuard } from './guards'

// The middleware for every page of the application.
const globalMiddleware = ['locale']

// Load middleware modules dynamically.
const routeMiddleware = resolveMiddleware(
  require.context('~/middleware', false, /.*\.js$/)
)

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior
})

// Apply guards in order
router.beforeEach(qrValidationGuard)
router.beforeEach(campaignStatusGuard)
router.beforeEach(middlewareGuard)
router.afterEach(afterEach)

export default router

/**
 * Middleware router guard.
 *
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
async function middlewareGuard (to, from, next) {
  // Get the matched components and resolve them.
  const components = await resolveComponents(
    to.matched.map(record => record.components?.default || record.component).filter(Boolean)
  )

  if (components.length === 0) {
    return next()
  }

  // Get the middleware for all the matched components.
  const middleware = getMiddleware(components)

  // Call each middleware.
  callMiddleware(middleware, to, from, (...args) => {
    // Set the application layout only if "next()" was called with no args.
    if (args.length === 0) {
      // In Vue 3, we don't have router.app.setLayout
      // This functionality should be handled differently or removed
      // router.app.setLayout(components[0].layout || '')
    }

    next(...args)
  })
}

/**
 * Global after hook.
 *
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
 async function afterEach (to, from, next) {
   // Vue 3 cleanup - loading state managed by LoadingOrchestrator service
   // Navigation completion handled automatically by Vue Router 4
 }

/**
 * Call each middleware.
 *
 * @param {Array} middleware
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
function callMiddleware (middleware, to, from, next) {
  const stack = middleware.reverse()

  const _next = (...args) => {
    // Stop if "_next" was called with an argument or the stack is empty.
    if (args.length > 0 || stack.length === 0) {
      // Removed problematic Vue 2 loading reference that was causing navigation blocking
      // Loading state is now managed by LoadingOrchestrator service

      return next(...args)
    }

    const middleware = stack.pop()

    if (typeof middleware === 'function') {
      middleware(to, from, _next)
    } else if (routeMiddleware[middleware]) {
      routeMiddleware[middleware](to, from, _next)
    } else {
      throw Error(`Undefined middleware [${middleware}]`)
    }
  }

  _next()
}

/**
 * Resolve async components.
 *
 * @param  {Array} components
 * @return {Array}
 */
function resolveComponents (components) {
  return Promise.all(components.map(component => {
    return typeof component === 'function' ? component() : component
  }))
}

/**
 * Merge the the global middleware with the components middleware.
 *
 * @param  {Array} components
 * @return {Array}
 */
function getMiddleware (components) {
  const middleware = [...globalMiddleware]

  components.filter(c => c.middleware).forEach(component => {
    if (Array.isArray(component.middleware)) {
      middleware.push(...component.middleware)
    } else {
      middleware.push(component.middleware)
    }
  })

  return middleware
}

/**
 * Scroll Behavior
 *
 * @link https://router.vuejs.org/en/advanced/scroll-behavior.html
 *
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior (to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition
  }

  if (to.hash) {
    return { el: to.hash }
  }

  // In Vue Router 4, we can check route meta or matched records
  const matchedRecord = to.matched[to.matched.length - 1]
  if (matchedRecord?.meta?.scrollToTop === false) {
    return {}
  }

  return { top: 0, left: 0 }
}

/**
 * @param  {Object} requireContext
 * @return {Object}
 */
function resolveMiddleware (requireContext) {
  return requireContext.keys()
    .map(file =>
      [file.replace(/(^.\/)|(\.js$)/g, ''), requireContext(file)]
    )
    .reduce((guards, [name, guard]) => (
      { ...guards, [name]: guard.default }
    ), {})
}
